import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import LogoMark from "@/components/Logo";
import { usePlatformName } from "@/brand";
import { ArrowRight, ShieldCheck, Zap, BarChart3, Eye, EyeOff } from "lucide-react";

const DEMO = [
  { label: "Owner", email: "fintexbee@gmail.com", pw: "Owner@123" },
  { label: "Admin", email: "admin@cleanmates.in", pw: "Admin@123" },
  { label: "Telecaller", email: "priya@cleanmates.in", pw: "Tele@123" },
  { label: "Accountant", email: "accounts@cleanmates.in", pw: "Acct@123" },
  { label: "Field Staff", email: "ravi@cleanmates.in", pw: "Field@123" },
];

const FEATURES = [
  { icon: Zap, text: "Leads → Quotes → Jobs → Billing, one flow" },
  { icon: ShieldCheck, text: "GPS & photo-verified field execution" },
  { icon: BarChart3, text: "Live analytics for every workspace" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const platformName = usePlatformName();
  const [email, setEmail] = useState(() => localStorage.getItem("cm_login_email") || "");
  const [password, setPassword] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [rememberLogin, setRememberLogin] = useState(() => !!localStorage.getItem("cm_login_email"));
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (rememberLogin && email) localStorage.setItem("cm_login_email", email);
    else localStorage.removeItem("cm_login_email");
  }, [email, rememberLogin]);

  const submit = async (e, em, pw) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(em || email, pw || password, em ? "" : companyCode);
      const owner = u.role === "super_admin" || u.is_workspace_owner;
      nav(owner ? (u.role === "super_admin" && !u.active_tenant_id ? "/choose-workspace" : "/admin") : "/welcome", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.12fr_0.88fr] bg-background" data-testid="login-page">
      <div className="hidden lg:flex relative overflow-hidden bg-brand-dark flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-100" style={{ background: "hsl(var(--brand-dark) / 0.32)" }} />
        <div className="relative flex items-center gap-3">
          <LogoMark size={40} />
          <div className="font-heading font-extrabold text-inverse text-2xl tracking-tight">{platformName}</div>
        </div>
        <div className="relative">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/15 bg-primary-tint/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground">
            <span className="status-dot bg-primary" />
            Live operations dashboard
          </div>
          <h1 className="font-heading text-5xl font-extrabold leading-[1.06] text-inverse">
            Run every brand.<br />
            <span className="text-gold">One command center.</span>
          </h1>
          <p className="mt-5 max-w-md text-base text-muted-foreground">The multi-tenant operations OS for field services and product businesses — leads, scheduling, verified execution, GST billing and analytics in one place.</p>
          <div className="mt-8 space-y-4">
            {FEATURES.map((f) => (
              <div key={f.text} className="flex items-center gap-3 rounded-2xl border border-border/10 bg-primary-tint/5 px-3 py-3 text-sm text-foreground shadow-soft">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-tint/10"><f.icon size={15} className="text-gold" /></div>
                {f.text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <svg viewBox="0 0 420 210" className="w-full max-w-md opacity-90" aria-hidden="true">
            <rect x="18" y="54" width="384" height="122" rx="22" fill="hsl(var(--surface) / 0.12)" stroke="hsl(var(--border) / 0.4)" />
            <rect x="42" y="84" width="122" height="52" rx="14" fill="hsl(var(--primary))" opacity="0.7" />
            <path d="M60 117C88 90 106 101 125 77L154 106V140H60V117Z" fill="hsl(var(--primary-hover))" opacity="0.9" />
            <rect x="190" y="76" width="180" height="14" rx="7" fill="hsl(var(--border) / 0.5)" />
            <rect x="190" y="102" width="142" height="12" rx="6" fill="hsl(var(--border) / 0.35)" />
            <rect x="190" y="122" width="160" height="12" rx="6" fill="hsl(var(--border) / 0.35)" />
            <rect x="190" y="150" width="120" height="10" rx="5" fill="hsl(var(--accent-gold))" opacity="0.9" />
          </svg>
        </div>
        <div className="relative text-muted-foreground text-xs">© {new Date().getFullYear()} {platformName}. All rights reserved.</div>
      </div>

      <div className="flex items-center justify-center bg-surface-sunken p-6 md:p-8">
        <div className="w-full max-w-md rounded-[28px] border border-border/80 bg-card/80 p-5 shadow-layered backdrop-blur-xl sm:p-7">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <LogoMark size={40} />
            <div className="font-heading font-extrabold text-xl">{platformName}</div>
          </div>
          <div className="mb-7">
            <h2 className="font-heading text-3xl font-extrabold tracking-tight" data-testid="login-heading">Welcome back</h2>
            <p className="mt-2 text-sm text-muted-foreground">Sign in to your workspace to continue.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="login-company-code" className="mb-1.5 block text-sm font-medium text-foreground">Company Code <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input id="login-company-code" data-testid="login-company-code-input" autoComplete="organization" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)}
                placeholder="e.g. CM or cleanmates" />
            </div>
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-foreground">Email or Login ID</label>
              <Input id="login-email" data-testid="login-email-input" type="text" autoComplete="username" aria-label="Email or Login ID" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com or login id" required />
            </div>
            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <Input id="login-password" data-testid="login-password-input" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" required className="pr-12" />
                <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-1.5 grid w-10 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={rememberLogin} onCheckedChange={(checked) => setRememberLogin(checked === true)} aria-label="Remember login ID on this device" />
              Remember login ID on this device
            </label>
            {error && <div data-testid="login-error" className="rounded-xl border border-danger bg-danger-tint px-3 py-2 text-sm text-danger-foreground dark:border-danger dark:bg-danger dark:text-danger-foreground">{error}</div>}
            <Button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full h-11 shadow-soft">
              {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} className="ml-1.5" /></>}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <button data-testid="google-signin-button" onClick={googleLogin}
            className="flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-border bg-card/80 text-sm font-medium text-foreground transition-all hover:bg-accent hover:shadow-sm">
            <span aria-hidden="true" className="font-heading text-lg font-extrabold leading-none text-primary">G</span>
            Sign in with Google
          </button>
          <div className="mt-2 text-center text-[11px] text-muted-foreground">Google sign-in works only for emails already registered by the Owner.</div>
          <div className="mt-8">
            <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Demo accounts</div>
            <div className="flex flex-wrap gap-2">
              {DEMO.map((d) => (
                <button key={d.label} data-testid={`demo-login-${d.label.toLowerCase().replace(" ", "-")}`}
                  onClick={(e) => submit(e, d.email, d.pw)}
                  className="rounded-full border border-border bg-card/80 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
